/**
 * @file useProductKindActions.ts
 * @description Действия по виду товара в «⋯» (phase19.22, W22): изменить,
 * в архив / из архива, удалить.
 *
 * Пункты — массивом-реестром (`RowAction[]`), деструктивное — последним.
 * После любой мутации список перечитывается: счётчики считает сервер.
 * 409 `product_kind_in_use` → тост «Вид используется в N товарах — можно
 * только архивировать» (архив — соседний пункт того же меню).
 */

import { Archive, ArchiveRestore, Pencil, Trash2 } from "lucide-react";
import { useCallback, useState } from "react";

import type { RowAction } from "../../ui/RowActionsMenu";
import { useNotify } from "../../ui/NotifyProvider";
import { ApiError } from "../../../lib/api-client";
import {
  productKindsApi,
  type ProductKind,
  type ProductKindInUseDetails,
} from "../../../lib/api-product-kinds";
import { pluralRu } from "../../../lib/plural";

interface Options {
  onEdit: (kind: ProductKind) => void;
  refresh: () => Promise<void>;
}

export function useProductKindActions({ onEdit, refresh }: Options) {
  const notify = useNotify();
  const [deleting, setDeleting] = useState<ProductKind | null>(null);
  const [busy, setBusy] = useState(false);

  const setArchived = useCallback(
    async (kind: ProductKind, archived: boolean) => {
      setBusy(true);
      try {
        if (archived) await productKindsApi.archive(kind.id);
        else await productKindsApi.restore(kind.id);
        notify.success(archived ? `Вид «${kind.name}» в архиве` : `Вид «${kind.name}» восстановлен`);
        await refresh();
      } catch (err) {
        notify.error(err instanceof ApiError ? err.message : "Не удалось изменить вид");
      } finally {
        setBusy(false);
      }
    },
    [notify, refresh],
  );

  const confirmDelete = useCallback(async () => {
    if (!deleting) return;
    const kind = deleting;
    setBusy(true);
    try {
      await productKindsApi.remove(kind.id);
      notify.success(`Вид «${kind.name}» удалён`);
      setDeleting(null);
      await refresh();
    } catch (err) {
      setDeleting(null);
      if (err instanceof ApiError && err.code === "product_kind_in_use") {
        const d = err.details as Partial<ProductKindInUseDetails> | undefined;
        notify.error(inUseText(d?.productsCount ?? 0, d?.categoriesCount ?? 0));
      } else {
        notify.error(err instanceof ApiError ? err.message : "Не удалось удалить вид");
      }
    } finally {
      setBusy(false);
    }
  }, [deleting, notify, refresh]);

  const actionsFor = useCallback(
    (kind: ProductKind): RowAction[] => [
      { key: "edit", label: "Изменить", icon: Pencil, run: () => onEdit(kind) },
      kind.isActive
        ? { key: "archive", label: "В архив", icon: Archive, disabled: busy, run: () => void setArchived(kind, true) }
        : { key: "restore", label: "Из архива", icon: ArchiveRestore, disabled: busy, run: () => void setArchived(kind, false) },
      { key: "delete", label: "Удалить", icon: Trash2, danger: true, disabled: busy, run: () => setDeleting(kind) },
    ],
    [busy, onEdit, setArchived],
  );

  return { actionsFor, deleting, setDeleting, confirmDelete, busy };
}

/** «Вид используется в 86 товарах и 3 категориях — можно только архивировать». */
function inUseText(products: number, categories: number): string {
  const parts: string[] = [];
  if (products > 0) parts.push(`${products} ${pluralRu(products, ["товаре", "товарах", "товарах"])}`);
  if (categories > 0) {
    parts.push(`${categories} ${pluralRu(categories, ["категории", "категориях", "категориях"])}`);
  }
  const where = parts.length > 0 ? parts.join(" и ") : "каталоге";
  return `Вид используется в ${where} — можно только архивировать.`;
}
